import { toast } from 'App';
import { isAxiosError } from 'axios';
import { $api } from './interceptor';
import { adminStore } from 'views/store/useAdminsStore';
import {
  TAdminData,
  TBaseAdminData,
  TUpdateAdminData,
} from 'views/types/admins';

const adminApi = () => {
  const getCurrentAdmin = async () => {
    try {
      adminStore.setState({ loading: true });
      const { data } = await $api.get<TAdminData>('/api/admin/current');
      adminStore.setState({ currentAdmin: data });

      return data;
    } catch (error) {
      adminStore.setState({ currentAdmin: null });
      errorHandler(error);
    } finally {
      adminStore.setState({ loading: false });
    }
  };

  const getAll = async (
    search?: string,
    filters?: { role?: string; status?: string; online?: boolean }
  ) => {
    try {
      adminStore.setState({ loading: true });

      const params: Record<string, string> = {};

      if (search) params.search = search;
      if (filters?.role) params.role = filters.role;
      if (filters?.status) params.status = filters.status;
      if (filters?.online !== undefined) params.online = String(filters.online);

      const { data } = await $api.get<TAdminData[]>('/api/admin', {
        params,
      });

      adminStore.setState({ admins: data });
      return data;
    } catch (error) {
      adminStore.setState({ admins: [] });
      errorHandler(error);
    } finally {
      adminStore.setState({ loading: false });
    }
  };

  const createAdmin = async (newAdmin: TBaseAdminData) => {
    try {
      adminStore.setState({ loading: true });
      const { data } = await $api.post<TAdminData>('/api/admin/create', {
        ...newAdmin,
      });

      adminStore.setState(state => ({ admins: [...state.admins, data] }));
      successNotify('Admin created successfully');
      return data;
    } catch (error) {
      errorHandler(error);
    } finally {
      adminStore.setState({ loading: false });
    }
  };

  const updateAdmin = async (updateData: TUpdateAdminData, id: string) => {
    try {
      adminStore.setState({ loading: true });
      const { data } = await $api.patch<TAdminData>(`/api/admin/${id}`, {
        ...updateData,
      });

      adminStore.setState(state => ({
        admins: [...state.admins.filter(admin => admin.id !== data.id), data],
      }));
      successNotify('Admin updated successfully');
      return data;
    } catch (error) {
      errorHandler(error);
    } finally {
      adminStore.setState({ loading: false });
    }
  };

  const deleteAdmin = async (id: string) => {
    try {
      adminStore.setState({ loading: true });
      await $api.delete<void>(`/api/admin/${id}`);

      successNotify('Admin deleted successfully');
      await getAll();
    } catch (error) {
      errorHandler(error);
    } finally {
      adminStore.setState({ loading: false });
    }
  };

  const getRoles = async () => {
    try {
      const { data } = await $api.get<
        { id: string; name: string; description: string }[]
      >('/api/admin/permissions/roles/basic');
      return data;
    } catch (error) {
      errorHandler(error);
      return [];
    }
  };

  const successNotify = (text: string) => {
    toast({
      title: 'Admin API',
      description: text,
      status: 'success',
      duration: 3000,
      isClosable: true,
      position: 'top-right',
    });
  };

  const getErrorMessage = (error: unknown): string => {
    let description = 'Something went wrong. Please try again later.';

    if (isAxiosError(error)) {
      const responseMessage = error.response?.data?.message;

      if (typeof responseMessage === 'string') {
        description = responseMessage;
      } else if (Array.isArray(responseMessage)) {
        description = responseMessage.join(', ');
      }
    }

    return description;
  };

  const errorHandler = (error: unknown) => {
    console.error(error);
    const description = getErrorMessage(error);

    if (description.includes('Data not found')) {
      return;
    }

    toast({
      title: 'Error',
      description,
      status: 'error',
      duration: 3000,
      isClosable: true,
      position: 'top-right',
    });
  };

  const restartDeposit = async (txHash: string) => {
    try {
      await $api.post('api/admin/restart-deposit', { txHash });
      toast({
        title: 'Transaction restarted',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
    } catch (error) {
      errorHandler(error);
    }
  };

  const restartWithdrawal = async (orderId: string) => {
    try {
      await $api.post('api/admin/restart-withdrawal', { orderId });
      toast({
        title: 'Transaction restarted',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
    } catch (error) {
      errorHandler(error);
    }
  };

  const cancelOrder = async (orderId: string, orderType: string) => {
    try {
      return await $api.post('/api/orders/cancel-order', {
        orderId,
        orderType,
      });
    } catch (error) {
      errorHandler(error);
    }
  };

  return {
    getCurrentAdmin,
    getAll,
    createAdmin,
    updateAdmin,
    deleteAdmin,
    getRoles,
    errorHandler,
    getErrorMessage,
    restartDeposit,
    restartWithdrawal,
    cancelOrder,
  };
};

export const adminService = adminApi();
